// tornar-recorrente-form.component.ts
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { FunctionsService } from 'src/app/data/functions.service';
import {
  AtualizacaoCompraRecorrente,
  CompraRecorrente,
  CriacaoCompraRecorrente,
  ItemCompraRecorrente,
  ItemTornarRecorrenteCru,
  ModoTornarRecorrenteModal,
  OrigemCompraRecorrente,
  Periodicidade,
  TerminoRecorrencia,
  itensCompraRecorrenteParaArray,
} from '../models/compra-recorrente.model';

/** Espelha exatamente as opções de condição/duração do passo 3 do stepper
 * (`adiciona-negociacao-composta.component.ts`) — mesmo vocabulário para o
 * comprador, mesmos valores aceitos pela API (`ItemCompraRecorrente`). */
const DURACOES: { label: string; codigo: '1' | '3' | '5' | '7' }[] = [
  { label: '1 Dia', codigo: '1' },
  { label: '3 Dias', codigo: '3' },
  { label: '5 Dias', codigo: '5' },
  { label: '7 Dias', codigo: '7' },
];

const PERIODICIDADES: Periodicidade[] = [
  'semanal',
  'quinzenal',
  'mensal',
  'bimestral',
  'trimestral',
  'semestral',
  'anual',
];

const NOME_MAX_LENGTH = 80;
const QUANTIDADE_MIN = 1;
const QUANTIDADE_MAX = 100;
const VALOR_PRETENDIDO_MAX = 999999.99;
const PRE_AVISO_DIAS_DEFAULT = 3;
const PRE_AVISO_DIAS_MIN = 0;
const PRE_AVISO_DIAS_MAX = 15;
const OCORRENCIAS_MIN = 1;
const OCORRENCIAS_MAX = 60;
/** Tempo para o toast de sucesso ficar visível antes de emitir `salvo` —
 * `MessageService`/`p-toast` são escopados a este componente, então emitir
 * `salvo` (e o hospedeiro fechar o dialog/avançar o stepper) na hora
 * destruiria o toast antes do comprador conseguir ler. */
const ATRASO_SALVO_APOS_SUCESSO_MS = 1100;

/**
 * Formulário de "Tornar compra de rotina" (E-44/T-260). Resolve
 * periodicidade/início/término/pré-aviso + a revisão (modo parametrizado)
 * ou parametrização por item (modo cru), e emite `salvo`/`cancelado`.
 *
 * Extraído do antigo `TornarRecorrenteModalComponent` em T-271 para
 * eliminar o diálogo aninhado (MatDialog dentro de MatDialog) quando a
 * origem é o stepper de `adiciona-negociacao-composta` — este componente é
 * **apresentacional e agnóstico de MatDialog** de propósito, para poder ser
 * hospedado tanto pelo shell `TornarRecorrentModalComponent`
 * (favoritos/tela de gestão, via `dialog.open(...)`) quanto pelo 4º
 * `mat-step` do stepper (origem `bulk-list-import`) sem depender de nenhuma
 * diretiva `mat-dialog-*`. Quem hospeda decide o que fazer com
 * `salvo`/`cancelado` (fechar o dialog, avançar o stepper).
 *
 * Visual conforme a spec de UX-1 (howl-san, `docs/design/compra-de-rotina-ux-spec.md` §3).
 */
@Component({
  selector: 'app-tornar-recorrente-form',
  templateUrl: './tornar-recorrente-form.component.html',
  styleUrls: ['./tornar-recorrente-form.component.css'],
  providers: [MessageService],
})
export class TornarRecorrenteFormComponent implements OnInit {
  @Input() itens: ItemCompraRecorrente[] | ItemTornarRecorrenteCru[] = [];
  @Input() modo: ModoTornarRecorrenteModal = 'parametrizado';
  @Input() origem: OrigemCompraRecorrente;
  /** Nome sugerido para a rotina — ex.: o nome da lista de favoritos ou um
   * default calculado pelo caller. Editável no formulário. */
  @Input() nomeSugerido?: string;
  /**
   * Presente só ao editar uma compra de rotina já existente (T-262, ação
   * "Editar" da tela de gestão) — quando preenchido: `nome`/`periodicidade`/
   * `data_inicio`/`termino`/`pre_aviso_dias`/`itens` pré-preenchem o
   * formulário (`itens`/`origem` recebidos à parte são ignorados) e
   * `salvar()` chama `atualizarCompraRecorrente` em vez de
   * `criarCompraRecorrente`.
   */
  @Input() compraExistente?: CompraRecorrente;

  /** Emite o id da compra de rotina criada/editada (pode ser `null` se a API
   * não devolver um) quando o salvamento é concluído com sucesso, depois do
   * atraso que dá tempo do toast aparecer. */
  @Output() salvo = new EventEmitter<string | null>();
  @Output() cancelado = new EventEmitter<void>();
  /** Espelha `salvando` para quem hospeda travar sua própria UI (ex.: o
   * botão de fechar do shell `TornarRecorrenteModalComponent`) enquanto uma
   * requisição está em voo — o estado de `salvando` mora só aqui dentro
   * desde a extração da T-271. */
  @Output() salvandoChange = new EventEmitter<boolean>();

  public form: FormGroup;
  public salvando = false;
  public erro: string | null = null;
  /** `minDate` do `p-datepicker` de início — não pode agendar no passado. */
  public hoje = new Date();

  public readonly duracoes = DURACOES;
  public readonly ocorrenciasMax = OCORRENCIAS_MAX;
  public opcoesPeriodicidade: { label: string; valor: Periodicidade }[] = [];
  public opcoesCondicao: { label: string; valor: 'novo' | 'usado' }[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private functions: FunctionsService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) { }

  ngOnInit(): void {
    this.opcoesPeriodicidade = PERIODICIDADES.map(periodicidade => ({
      label: this.translocoService.translate(`compra_rotina_periodicidade_${periodicidade}`),
      valor: periodicidade,
    }));
    this.opcoesCondicao = [
      { label: this.translocoService.translate('novo'), valor: 'novo' },
      { label: this.translocoService.translate('usado'), valor: 'usado' },
    ];

    this.montarForm();
  }

  private montarForm(): void {
    const existente = this.compraExistente;

    this.form = this.formBuilder.group({
      nome: [
        (existente?.nome ?? this.nomeSugerido ?? '').slice(0, NOME_MAX_LENGTH),
        [Validators.required, Validators.maxLength(NOME_MAX_LENGTH)],
      ],
      periodicidade: [existente?.periodicidade ?? 'mensal', Validators.required],
      data_inicio: [
        existente ? this.deDataOnly(existente.data_inicio) : this.hoje,
        // Editando, `data_inicio` pode legitimamente já estar no passado (a
        // rotina já começou) — a API só exige "não anterior a hoje" na
        // CRIAÇÃO (`POST`), o `PUT` aceita qualquer data e só recalcula a
        // próxima execução sem nunca voltar para o passado. Aplicar o mesmo
        // validator aqui deixaria o formulário inválido só de abrir.
        this.ehEdicao ? [Validators.required] : [Validators.required, this.validarDataNaoAnteriorAHoje],
      ],
      termino: this.formBuilder.group(
        {
          tipo: existente?.termino?.tipo ?? 'nunca',
          data: existente?.termino?.data ? this.deDataOnly(existente.termino.data) : null,
          ocorrencias: existente?.termino?.ocorrencias ?? null,
        },
        { validators: this.validarTermino },
      ),
      pre_aviso_dias: [
        existente?.pre_aviso_dias ?? PRE_AVISO_DIAS_DEFAULT,
        [Validators.required, Validators.min(PRE_AVISO_DIAS_MIN), Validators.max(PRE_AVISO_DIAS_MAX)],
      ],
      itens: this.formBuilder.array(this.montarItensIniciais()),
    });

    // A validação de "término em data > início" depende de `data_inicio`,
    // que vive fora do grupo `termino` — sem isso, mudar o início não
    // reavaliava a validade do término (Angular só reavalia um FormGroup
    // quando um dos SEUS próprios controles muda).
    this.form.get('data_inicio').valueChanges.subscribe(() => {
      this.form.get('termino').updateValueAndValidity();
    });
  }

  private validarDataNaoAnteriorAHoje = (control: AbstractControl): ValidationErrors | null => {
    const valor: Date | null = control.value;
    if (!valor) {
      return null;
    }
    return this.inicioDoDia(valor) < this.inicioDoDia(new Date()) ? { dataAnteriorAHoje: true } : null;
  };

  private validarTermino: ValidatorFn = (grupo: AbstractControl): ValidationErrors | null => {
    const tipo = grupo.get('tipo')?.value;

    if (tipo === 'em_data') {
      const data: Date | null = grupo.get('data')?.value;
      if (!data) {
        return { terminoDataObrigatoria: true };
      }
      const inicio: Date | null = this.form?.get('data_inicio')?.value;
      if (inicio && this.inicioDoDia(data) <= this.inicioDoDia(inicio)) {
        return { terminoAntesOuIgualAoInicio: true };
      }
    }

    if (tipo === 'apos_n') {
      const ocorrencias = grupo.get('ocorrencias')?.value;
      if (!ocorrencias || ocorrencias < OCORRENCIAS_MIN) {
        return { ocorrenciasInvalidas: true };
      }
    }

    return null;
  };

  private inicioDoDia(data: Date): number {
    const copia = new Date(data);
    copia.setHours(0, 0, 0, 0);
    return copia.getTime();
  }

  get ehEdicao(): boolean {
    return !!this.compraExistente;
  }

  get itensParametrizados(): ItemCompraRecorrente[] {
    return this.modo === 'parametrizado' ? (this.itens as ItemCompraRecorrente[]) : [];
  }

  get itensCrus(): ItemTornarRecorrenteCru[] {
    return this.modo === 'cru' ? (this.itens as ItemTornarRecorrenteCru[]) : [];
  }

  get itensFormArray(): FormArray {
    return this.form?.get('itens') as FormArray;
  }

  itemFormGroup(index: number): FormGroup {
    return this.itensFormArray?.at(index) as FormGroup;
  }

  get terminoTipo(): string {
    return this.form?.get('termino')?.get('tipo')?.value;
  }

  get formValido(): boolean {
    return !!this.form && this.form.valid;
  }

  private montarItemFormCru(item: ItemTornarRecorrenteCru): FormGroup {
    return this.formBuilder.group({
      ean: [item.ean],
      nome: [item.nome],
      imagemUrl: [item.imagemUrl || null],
      quantidade: [1, [Validators.required, Validators.min(QUANTIDADE_MIN), Validators.max(QUANTIDADE_MAX)]],
      condicao: ['novo', Validators.required],
      duracao: ['3', Validators.required],
      valor_pretendido: [0, [Validators.min(0), Validators.max(VALOR_PRETENDIDO_MAX)]],
      encerrar_automaticamente: [false],
    });
  }

  /** Mesmo shape de `montarItemFormCru`, mas pré-preenchido com os valores
   * reais de um item já persistido (vindo de `GET /:id`, editando). */
  private montarItemFormEdicao(item: ItemCompraRecorrente): FormGroup {
    return this.formBuilder.group({
      ean: [item.ean],
      nome: [item.nome],
      imagemUrl: [null],
      quantidade: [item.quantidade, [Validators.required, Validators.min(QUANTIDADE_MIN), Validators.max(QUANTIDADE_MAX)]],
      condicao: [item.novo ? 'novo' : 'usado', Validators.required],
      duracao: [item.duracao, Validators.required],
      valor_pretendido: [item.valor_pretendido, [Validators.min(0), Validators.max(VALOR_PRETENDIDO_MAX)]],
      encerrar_automaticamente: [!!item.encerrar_automaticamente],
    });
  }

  private montarItensIniciais(): FormGroup[] {
    if (this.ehEdicao) {
      return itensCompraRecorrenteParaArray(this.compraExistente.itens).map(item => this.montarItemFormEdicao(item));
    }
    if (this.modo === 'cru') {
      return this.itensCrus.map(item => this.montarItemFormCru(item));
    }
    return [];
  }

  /** `Date` do `p-datepicker` → string date-only `'YYYY-MM-DD'`, sem passar
   * por `toISOString()` (que serializa em UTC e pode voltar um dia — o
   * Brasil inteiro está atrás de UTC, então `toISOString()` de meia-noite
   * local sempre cai no dia anterior em UTC). */
  private paraDataOnly(data: Date | string | null): string | null {
    if (!data) {
      return null;
    }
    const objeto = data instanceof Date ? data : new Date(data);
    const ano = objeto.getFullYear();
    const mes = String(objeto.getMonth() + 1).padStart(2, '0');
    const dia = String(objeto.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  /** Inverso de `paraDataOnly`: `'YYYY-MM-DD'` → `Date` à meia-noite local,
   * sem passar por `new Date('YYYY-MM-DD')` (que o motor JS trata como UTC e
   * também pode voltar um dia em fusos negativos como o do Brasil). */
  private deDataOnly(dataOnly: string | null | undefined): Date | null {
    if (!dataOnly) {
      return null;
    }
    const [ano, mes, dia] = dataOnly.split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  private montarTermino(): TerminoRecorrencia {
    // `getRawValue()`, não `.value` — o form é desabilitado em `salvar()`
    // antes de montar o payload, e um `FormGroup.value` omite os campos de
    // filhos desabilitados (voltaria `undefined` aqui).
    const valor = this.form.getRawValue().termino;
    return {
      tipo: valor.tipo,
      data: valor.tipo === 'em_data' ? this.paraDataOnly(valor.data) : null,
      ocorrencias: valor.tipo === 'apos_n' ? Number(valor.ocorrencias) : null,
    };
  }

  private montarItensPayload(): ItemCompraRecorrente[] {
    if (this.modo === 'parametrizado') {
      return this.itensParametrizados;
    }

    return this.itensFormArray.controls.map((controle: AbstractControl) => {
      const valor = controle.value;
      const item: ItemCompraRecorrente = {
        ean: valor.ean,
        nome: valor.nome,
        quantidade: Number(valor.quantidade),
        novo: valor.condicao === 'novo',
        usado: valor.condicao === 'usado',
        duracao: valor.duracao,
        valor_pretendido: Number(valor.valor_pretendido) || 0,
        encerrar_automaticamente: !!valor.encerrar_automaticamente,
      };
      return item;
    });
  }

  cancelar(): void {
    if (this.salvando) {
      return;
    }
    this.cancelado.emit();
  }

  private setSalvando(valor: boolean): void {
    this.salvando = valor;
    this.salvandoChange.emit(valor);
  }

  salvar(): void {
    if (this.salvando) {
      return;
    }
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.erro = null;
    this.setSalvando(true);
    this.form.disable();

    const camposComuns = {
      nome: (this.form.getRawValue().nome || '').trim(),
      periodicidade: this.form.getRawValue().periodicidade,
      data_inicio: this.paraDataOnly(this.form.getRawValue().data_inicio),
      termino: this.montarTermino(),
      pre_aviso_dias: Number(this.form.getRawValue().pre_aviso_dias),
      itens: this.montarItensPayload(),
    };

    if (this.ehEdicao) {
      const payload: AtualizacaoCompraRecorrente = camposComuns;
      this.functions.atualizarCompraRecorrente(this.compraExistente.id, payload).toPromise()
        .then(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translocoService.translate('compra_rotina_acao_sucesso'),
            life: ATRASO_SALVO_APOS_SUCESSO_MS + 3000,
          });
          setTimeout(() => this.salvo.emit(this.compraExistente.id), ATRASO_SALVO_APOS_SUCESSO_MS);
        })
        .catch(error => {
          console.warn('Não foi possível editar a compra de rotina:', error);
          this.erro = this.translocoService.translate('compra_rotina_erro_generico');
          this.setSalvando(false);
          this.form.enable();
        });
      return;
    }

    const payload: CriacaoCompraRecorrente = { ...camposComuns, origem: this.origem };

    this.functions.criarCompraRecorrente(payload).toPromise()
      .then(res => {
        const id = res?.body?.data || null;
        this.messageService.add({
          severity: 'success',
          summary: this.translocoService.translate('compra_rotina_criar_sucesso'),
          life: ATRASO_SALVO_APOS_SUCESSO_MS + 3000,
        });
        setTimeout(() => this.salvo.emit(id), ATRASO_SALVO_APOS_SUCESSO_MS);
      })
      .catch(error => {
        console.warn('Não foi possível criar a compra de rotina:', error);
        this.erro = this.translocoService.translate('compra_rotina_criar_erro');
        this.setSalvando(false);
        this.form.enable();
      });
  }
}
